import type { FocusEvent } from "react";
import type { BoardPlacement } from "./Board.types";
import { getCellKeySuffix, getItemKeyPrefix } from "./boardCells";

/** A blur that takes focus out of the element handling it, or off the page. */
export const hasFocusLeft = ({
    relatedTarget,
    currentTarget,
}: FocusEvent<HTMLElement>) =>
    !(relatedTarget instanceof Node) || !currentTarget.contains(relatedTarget);

/** Set on each card's `li`: its placement key. */
export const PLACEMENT_KEY_ATTRIBUTE = "data-placement-key";

/** Set on the root of each `BoardCard`: the element that takes focus. */
export const BOARD_CARD_ATTRIBUTE = "data-board-card";

export const BOARD_CARD_PROPS = { [BOARD_CARD_ATTRIBUTE]: "" };

/** Set on each `BoardCard`'s body: the card without its drawer. */
export const BOARD_CARD_SURFACE_ATTRIBUTE = "data-board-card-surface";

export const BOARD_CARD_SURFACE_PROPS = { [BOARD_CARD_SURFACE_ATTRIBUTE]: "" };

/** Set on a `BoardCard`'s drawer while it is shown, opening or closing. */
export const BOARD_CARD_DRAWER_ATTRIBUTE = "data-board-card-drawer";

export const BOARD_CARD_DRAWER_PROPS = { [BOARD_CARD_DRAWER_ATTRIBUTE]: "" };

/** The first mounted card of `itemId` in `root`, wherever a change moved it. */
export const findItemCard = (root: ParentNode, itemId: string) =>
    Array.from(root.querySelectorAll(`[${PLACEMENT_KEY_ATTRIBUTE}]`))
        .find((slot) =>
            slot
                .getAttribute(PLACEMENT_KEY_ATTRIBUTE)
                ?.startsWith(getItemKeyPrefix(itemId)),
        )
        ?.querySelector<HTMLElement>(`[${BOARD_CARD_ATTRIBUTE}]`) ?? null;

/** The mounted cards in `placement`'s cell in `root`, in page order. */
export const findCellCards = (root: ParentNode, placement: BoardPlacement) => {
    const cellKeySuffix = getCellKeySuffix(placement);
    return Array.from(root.querySelectorAll(`[${PLACEMENT_KEY_ATTRIBUTE}]`))
        .filter((slot) =>
            slot.getAttribute(PLACEMENT_KEY_ATTRIBUTE)?.endsWith(cellKeySuffix),
        )
        .flatMap(
            (slot) =>
                slot.querySelector<HTMLElement>(`[${BOARD_CARD_ATTRIBUTE}]`) ??
                [],
        );
};

/** Set on the element cards scroll in: a column, or the board with lanes. */
export const SCROLLER_ATTRIBUTE = "data-board-scroller";

export const BOARD_SCROLLER_PROPS = { [SCROLLER_ATTRIBUTE]: "" };

/** Set on each lane block: its lane id. */
export const LANE_ID_ATTRIBUTE = "data-lane-id";

/** Props for a card's `li`, so the board can find it by placement key. */
export const placementKeyProps = (placementKey: string) => ({
    [PLACEMENT_KEY_ATTRIBUTE]: placementKey,
});

export const laneIdProps = (laneId: string) => ({
    [LANE_ID_ATTRIBUTE]: laneId,
});
